/**
 * Volume Weighted Moving Average (VWMA)
 *
 * Like a SMA but each bar's price is weighted by its trading volume.
 * Formula: VWMA(n) = Σ(Closeᵢ × Volumeᵢ) / Σ(Volumeᵢ) over `period` bars.
 *
 * Category: Trend | Pane: Overlay
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { getSource } from "./sma";

export const vwmaIndicator: IndicatorDefinition = {
  id: "vwma",
  name: "Volume Weighted Moving Average",
  shortName: "VWMA",
  category: "trend",
  pane: "overlay",
  params: [
    { key: "period", label: "Period", type: "number", default: 20, min: 1, max: 500 },
    { key: "source", label: "Source", type: "select", default: "close", options: [
      { label: "Close", value: "close" }, { label: "Open", value: "open" },
      { label: "High", value: "high" }, { label: "Low", value: "low" },
      { label: "HL2", value: "hl2" }, { label: "HLC3", value: "hlc3" },
    ]},
    { key: "color", label: "Color", type: "color", default: "#EC407A" },
  ],
  plots: [
    { key: "vwma", label: "VWMA", color: "#EC407A", style: "line", lineWidth: 2 },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { period, source = "close" } = params;
    const src = getSource(candles, source);
    const data: { time: number; value: number }[] = [];

    let sumPV = 0; // price * volume
    let sumV = 0;  // volume

    for (let i = 0; i < src.length; i++) {
      const vol = candles[i].volume || 0;
      sumPV += src[i] * vol;
      sumV += vol;

      if (i >= period) {
        const oldVol = candles[i - period].volume || 0;
        sumPV -= src[i - period] * oldVol;
        sumV -= oldVol;
      }

      if (i >= period - 1) {
        const vwma = sumV !== 0 ? sumPV / sumV : src[i];
        data.push({ time: candles[i].time, value: vwma });
      }
    }

    return [{ key: "vwma", data }];
  },
};
